// Logica pura di My Bookings (testabile senza React).

// Filtri di stato, nell'ordine della spec.
export const FILTERS = ["all", "pending", "confirmed", "arrived", "no_show", "cancelled"];

// Stati che occupano posto (come ACTIVE nel backend).
export const ACTIVE = ["pending", "confirmed", "arrived"];

// Le vecchie "declined" si trattano come Cancellata.
export const statusKey = (status) => (status === "declined" ? "cancelled" : status);

// Azioni sulla riga per stato (tabella della spec): chiave del testo e stato di arrivo.
export const actionsFor = (status) => {
  switch (statusKey(status)) {
    case "pending":
      return [{ key: "confirm", to: "confirmed" }, { key: "reject", to: "cancelled" }];
    case "confirmed":
      return [{ key: "arrived", to: "arrived" }, { key: "noShow", to: "no_show" }];
    case "arrived":
    case "no_show":
      return [{ key: "undo", to: "confirmed" }];
    case "cancelled":
      return [{ key: "restore", to: "confirmed" }];
    default:
      return [];
  }
};

// Righe attenuate: No-show e Cancellata.
export const faded = (status) => ["no_show", "cancelled"].includes(statusKey(status));

export const filterBookings = (list, filter) =>
  filter === "all" ? list : list.filter((b) => statusKey(b.status) === filter);

export const countByStatus = (list) => {
  const out = { all: list.length };
  list.forEach((b) => {
    const k = statusKey(b.status);
    out[k] = (out[k] || 0) + 1;
  });
  return out;
};

// Raggruppa per fascia mantenendo l'ordine degli orari; `shifts` arriva dal backend.
// Le fasce chiuse (giorno speciale) senza prenotazioni non compaiono; quelle fuori fascia vanno in coda.
export const groupByShift = (list, shifts) => {
  const groups = shifts.map((s) => ({ shift: s, items: [] }));
  const other = { shift: null, items: [] };
  list.forEach((b) => {
    const g = groups.find((x) => x.shift.id === b.shift);
    (g || other).items.push(b);
  });
  const sorted = groups.filter((g) => g.items.length || !g.shift.closed);
  if (other.items.length) sorted.push(other);
  sorted.forEach((g) => g.items.sort((a, c) => a.time.localeCompare(c.time)));
  return sorted;
};

export const addDays = (d, n) => {
  const x = new Date(d + "T12:00:00");
  x.setDate(x.getDate() + n);
  return x.toLocaleDateString("sv-SE");
};

// 0 = lunedì ... 6 = domenica (come il backend).
export const weekdayOf = (d) => (new Date(d + "T12:00:00").getDay() + 6) % 7;

// Giorni rapidi: Oggi, Domani, i giorni del weekend entro 6 giorni (al massimo tre), Tra 7 giorni; senza doppioni.
export const quickDays = (today) => {
  const out = [
    { key: "today", date: today },
    { key: "tomorrow", date: addDays(today, 1) },
  ];
  let weekend = 0;
  for (let i = 0; i <= 6 && weekend < 3; i++) {
    const d = addDays(today, i);
    const wd = weekdayOf(d);
    if (wd < 5) continue;
    weekend += 1;
    if (!out.some((x) => x.date === d)) out.push({ key: "weekday", date: d, weekday: wd });
  }
  out.push({ key: "in7", date: addDays(today, 7) });
  return out;
};

// Percentuale di riempimento limitata a 100 per la barra, `over` se supera i coperti.
export const fill = (covers, capacity) => ({
  pct: capacity ? Math.min(100, Math.round((covers / capacity) * 100)) : 0,
  over: capacity > 0 && covers > capacity,
});

// L'orario scelto è uno degli orari delle fasce indicate.
export const timeInSlots = (shifts, time) => !!time && shifts.some((s) => s.slots.some((x) => x.time === time));

// Fasce proposte nella finestra: quelle aperte quel giorno; se non ce n'è nessuna, tutte le fasce attive.
export const bandsForDay = (shifts) => {
  const open = shifts.filter((s) => s.open_today);
  return open.length ? open : shifts;
};

// Fascia che contiene l'orario (tra gli orari proposti, altrimenti tra inizio e fine).
export const bandOfTime = (bands, time) =>
  (time && (bands.find((b) => b.slots.some((x) => x.time === time)) ||
    bands.find((b) => b.start_time <= time && time < b.end_time))) || null;

// Fascia proposta per una nuova prenotazione: oggi la prima non ancora finita, altrimenti la prima.
export const defaultBand = (bands, isToday, now) => {
  if (!bands.length) return null;
  if (isToday) return bands.find((b) => b.end_time > now) || bands[bands.length - 1];
  return bands[0];
};

export const freeSeats = (band) => (band ? Math.max(0, band.capacity - band.used) : 0);

// Posti mancanti per il numero di persone (0 = c'è posto).
export const missingSeats = (persons, band) => (band ? Math.max(0, Number(persons || 0) - freeSeats(band)) : 0);

// Scadenza della pausa: ora locale "HH:MM" e se coincide con la mezzanotte (riapre domani).
export const pauseUntil = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const time = `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
  return { time, midnight: time === "00:00" };
};

// Nomi dei canali per i messaggi.
export const CHANNEL_NAMES = { email: "Email", sms: "SMS", whatsapp: "WhatsApp" };
export const channelNames = (channels) => (channels || []).map((c) => CHANNEL_NAMES[c] || c);

// Tavoli "In uso" raggruppati per sala, per il menu Tavolo.
export const tableGroups = (rooms) =>
  (rooms || [])
    .map((r) => ({ id: r.id, name: r.name, tables: (r.tables || []).filter((t) => t.active !== false) }))
    .filter((r) => r.tables.length);
